import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import './Releases.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Releases() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    tebex_url: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const toast = useToast();

  useEffect(() => {
    fetchReleases();
  }, []);

  const fetchReleases = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/releases`);
      const releasesData = response.data.map(r => ({
        ...r,
        tags: r.tags ? JSON.parse(r.tags) : []
      }));
      setReleases(releasesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching releases:', error);
      toast.error('Failed to load releases');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingRelease) {
        await axios.put(`${API_URL}/api/releases/${editingRelease.id}`, formData, {
          withCredentials: true
        });
        toast.success('Release updated successfully');
      } else {
        await axios.post(`${API_URL}/api/releases`, formData, {
          withCredentials: true
        });
        toast.success('Release created successfully');
      }
      
      setShowModal(false);
      setEditingRelease(null);
      setFormData({
        title: '',
        description: '',
        image_url: '',
        tebex_url: '',
        tags: []
      });
      fetchReleases();
    } catch (error) {
      console.error('Error saving release:', error);
      toast.error(error.response?.data?.error || 'Failed to save release');
    }
  };

  const handleEdit = (release) => {
    setEditingRelease(release);
    setFormData({
      title: release.title,
      description: release.description,
      image_url: release.image_url || '',
      tebex_url: release.tebex_url || '',
      tags: release.tags || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this release?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/releases/${id}`, {
        withCredentials: true
      });
      toast.success('Release deleted successfully');
      fetchReleases();
    } catch (error) {
      console.error('Error deleting release:', error);
      toast.error('Failed to delete release');
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  if (loading) {
    return <div className="loading">Loading releases...</div>;
  }

  return (
    <div className="admin-releases">
      <div className="admin-header">
        <h1>Releases Management</h1>
        <button className="btn-primary" onClick={() => {
          setEditingRelease(null);
          setFormData({
            title: '',
            description: '',
            image_url: '',
            tebex_url: '',
            tags: []
          });
          setShowModal(true);
        }}>
          Add Release
        </button>
      </div>

      <div className="releases-grid">
        {releases.map((item) => (
          <div key={item.id} className="release-card glass-strong">
            {item.image_url && (
              <div className="release-image" style={{ backgroundImage: `url(${item.image_url})` }}></div>
            )}
            <div className="release-content">
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              {item.tags && item.tags.length > 0 && (
                <div className="release-tags">
                  {item.tags.map((tag, idx) => (
                    <span key={idx} className="tag">{tag}</span>
                  ))}
                </div>
              )}
              {item.tebex_url && (
                <a href={item.tebex_url} target="_blank" rel="noopener noreferrer" className="release-link">
                  View on Tebex →
                </a>
              )}
              <div className="release-meta">
                <span className="author">By {item.author_name}</span>
                <span className="date">{new Date(item.created_at).toLocaleDateString()}</span>
              </div>
              <div className="release-actions">
                <button className="btn-edit" onClick={() => handleEdit(item)}>
                  Edit
                </button>
                <button className="btn-delete" onClick={() => handleDelete(item.id)}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content glass-strong" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRelease ? 'Edit Release' : 'Add Release'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter release title"
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  placeholder="Enter release description"
                />
              </div>

              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.png"
                />
              </div>

              <div className="form-group">
                <label>Tebex URL</label>
                <input
                  type="url"
                  name="tebex_url"
                  value={formData.tebex_url}
                  onChange={handleInputChange}
                  placeholder="https://devcore.tebex.io/package/..."
                />
              </div>

              <div className="form-group">
                <label>Tags</label>
                <div className="tag-input-container">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag and press Enter"
                  />
                  <button type="button" onClick={addTag} className="add-tag-btn">Add</button>
                </div>
                <div className="tags-list">
                  {formData.tags.map((tag, idx) => (
                    <span key={idx} className="tag">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingRelease ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Releases;
